<script setup lang="ts">
import { watch, computed, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import { useTheme } from './composables/useTheme'
import { setTeamForcedSource } from './composables/usePhonePrivacy'
import { useNotificationsStore } from './stores/notifications'
import { useUserStore } from './stores/user'
import CommandPalette from './components/CommandPalette.vue'
import MessageFlowInspector from './components/MessageFlowInspector.vue'
import OnboardingWizard from './components/OnboardingWizard.vue'

useTheme()

// Trigger fast-path deploy test
const toast = useToast()
const notifications = useNotificationsStore()
const userStore = useUserStore()

// Bridge the user store → usePhonePrivacy composable. The composable
// stays Pinia-agnostic (so unit tests don't need a Pinia harness); we
// inject the reactive accessor exactly once at bootstrap. Subsequent
// /auth/me refetches mutate userStore.user — every read of the getter
// inside a Vue computed picks up the new value automatically.
setTeamForcedSource(() => userStore.user?.effectiveMaskContactPhones === true)

const onboardingDismissed = ref(!!localStorage.getItem('neogo_onboarding_done'))

// Show wizard when authenticated user has not completed onboarding.
// Requires a hydrated user (not just a token in localStorage) to avoid
// flashing the wizard on top of an empty layout while a stale token is
// being rejected by the backend.
const showOnboarding = computed(
  () => userStore.isAuthenticated && !!userStore.user && !onboardingDismissed.value,
)

// React to localStorage changes from the wizard component
watch(
  () => userStore.isAuthenticated,
  (auth) => {
    if (!auth) {
      onboardingDismissed.value = true
    } else {
      onboardingDismissed.value = !!localStorage.getItem('neogo_onboarding_done')
    }
  },
)

// Bridge notifications store → PrimeVue Toast
watch(
  () => notifications.queue.length,
  () => {
    while (notifications.queue.length > 0) {
      const msg = notifications.queue.shift()
      if (msg) {
        toast.add({
          severity: msg.severity,
          summary: msg.summary,
          detail: msg.detail,
          life: msg.life,
        })
      }
    }
  },
)
</script>

<template>
  <router-view />
  <CommandPalette />
  <MessageFlowInspector />
  <OnboardingWizard v-if="showOnboarding" @complete="onboardingDismissed = true" />
  <Toast position="top-right" />
</template>

<style>
/* Base styles moved to theme/neogo-theme.css */
</style>
